import { v4 as uuid } from "uuid";
import NotebookCellMetadata from "../connector/extensions/types/ipynb/NotebookCellMetadata";
import NotebookCellType from "../connector/extensions/types/ipynb/NotebookCellType";
import NotebookContent from "../connector/extensions/types/ipynb/NotebookContent";
import {
  DEFAULT_PYTHON_METADATA,
  DEFAULT_R_METADATA,
  NB_FORMATS,
} from "../session/JupyterSource";
import { LanguageName } from "../types/language";
import { SourceType } from "../types/SourceType";

const CELL_DELIMITER = "# %%";
const MARKDOWN_COMMENT = "# ";

const cellRegex = /^# %%(?![A-Za-z])([\s\S]*?)(?=\n# %%(?![A-Za-z]))/gm;
const typeRegex = /^[^\{]*\[([^\]]*)\]/;
const metadataRegex = /\{.*\}/;
const commentRegex = /^# /gm;

type NotebookCellMetadataKey = keyof NotebookCellMetadata;
const standardKeys: NotebookCellMetadataKey[] = [
  "_cell_guid",
  "_uuid",
  "trusted",
  "collapsed",
];

export function convertToScript(ipynb: NotebookContent): NotebookContent {
  const source = ipynb.cells
    .map(cell => {
      // Strip out standard metadata fields.
      let includeMetadata = false;
      const metadata = JSON.stringify(cell.metadata, (key, val) => {
        if (val === cell.metadata) {
          // Need to return root metadata object to prevent early return.
          return val;
        }
        if (standardKeys.includes(key)) {
          return undefined;
        }
        includeMetadata = true;
        return val;
      });

      const cellSource =
        (Array.isArray(cell.source) ? cell.source.join("") : cell.source) || "";

      const converted =
        `${CELL_DELIMITER} [${cell.cell_type}]${
          includeMetadata ? " " + metadata : ""
        }\n` +
        `${
          cell.cell_type === "code"
            ? cellSource
            : cellSource.replace(/^/gm, MARKDOWN_COMMENT)
        }`;

      return converted;
    })
    .join("\n\n");

  const script: NotebookContent = {
    ...ipynb,
    cells: [
      {
        cell_type: "code",
        source,
        execution_count: 0,
        metadata: {
          _uuid: uuid(),
          _cell_guid: uuid(),
          trusted: true,
          collapsed: false,
        },
        outputs: [],
      },
    ],
  };

  return script;
}

export function convertToNotebook(ipynb: NotebookContent): NotebookContent {
  const notebook: NotebookContent = { ...ipynb, cells: [] };

  // Should only receive one-celled NB, but collect all sources in case.
  let script = ipynb.cells.map(cell => cell.source).join("\n");

  // Add regex sentinel to ensure script starts and ends with a cell delimiter.
  script = script.startsWith(CELL_DELIMITER)
    ? `${script}\n${CELL_DELIMITER}`
    : `${CELL_DELIMITER}\n${script}\n${CELL_DELIMITER}`;

  while (true) {
    const match = cellRegex.exec(script);

    if (!match) {
      break;
    }

    const content = match[1];
    const endOfHead = content.indexOf("\n");
    const head = content.substring(0, endOfHead);
    const body = content.substring(endOfHead + 1);
    const type = (typeRegex.exec(head) || ["", "code"])[1] as NotebookCellType;
    const metadata = JSON.parse(
      (head.match(metadataRegex) || ["{}"])[0]
    ) as NotebookCellMetadata;
    const source = (type === "code"
      ? body
      : body.replace(commentRegex, "")
    ).trimRight();

    notebook.cells.push({
      metadata: {
        // @ts-ignore TODO(b/156496389): Verify splat position is intentional
        _uuid: uuid(),
        // @ts-ignore
        _cell_guid: uuid(),
        // @ts-ignore
        trusted: true,
        collapsed: false,
        ...metadata,
      },
      cell_type: type,
      source,
      execution_count: 0,
      outputs: [],
    });
  }

  return notebook;
}

/** Converts raw code to a notebook / script ipynb with chosen languages metadata. */
export function convertCodeToIpynb(
  source: string,
  sourceType: SourceType,
  languageName: LanguageName
): NotebookContent {
  const ipynb: NotebookContent = {
    cells: [
      {
        source,
        cell_type: "code",
        execution_count: 0,
        outputs: [],
        metadata: {
          _uuid: uuid(),
          _cell_guid: uuid(),
          trusted: true,
          collapsed: false,
        },
      },
    ],
    metadata:
      languageName === "python" ? DEFAULT_PYTHON_METADATA : DEFAULT_R_METADATA,
    ...NB_FORMATS,
  };
  return sourceType === "script"
    ? convertToScript(ipynb)
    : convertToNotebook(ipynb);
}
